// Estado inicial del flow de aporte de producto. Dos CTAs apiladas
// verticalmente (uno arriba, otro abajo) — no en fila, porque en el
// panel lateral desktop el ancho es limitado y compartir el espacio
// entre dos botones full-width los achica demasiado.
//
// Tercer CTA opcional "Seleccionar anteriores" — se muestra solo si hay
// productos guardados en localStorage de la última generación exitosa.

import React, { useRef } from 'react';
import { Package, Upload } from 'lucide-react';
import { useUserProducts } from '../productsContext';
import type { MockProductV2, PresetV2 } from '../mockData';

export interface ChooseProductPanelProps {
  onShowProducts: () => void;
  onStartUpload: (files: FileList | File[]) => void;
  /** Atajo: confirma directo con productos de la última generación. */
  onUseLast: (products: MockProductV2[]) => void;
  /** Kind del preset abierto — controla la copy gris (el texto cambia
   *  para que sea consistente con el modo: template/design hablan de
   *  "preset", design-modelo de "elegir modelo / pose / fondo",
   *  design-auto de "armar la escena automáticamente"). */
  presetKind?: PresetV2['kind'];
}

export const ChooseProductPanel: React.FC<ChooseProductPanelProps> = ({
  onShowProducts,
  onStartUpload,
  onUseLast,
  presetKind,
}) => {
  // Input hidden — el botón "Subir imagen" dispara su click() para abrir
  // el file picker nativo. multiple=true para permitir batch upload.
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Atajo "Seleccionar anteriores": leemos del localStorage los ids guardados en la
  // última generación exitosa y los matcheamos contra el catálogo actual
  // del user. Si alguno fue borrado / renombrado, lo filtramos out.
  const allProducts = useUserProducts();
  const lastProducts = React.useMemo<MockProductV2[]>(() => {
    try {
      const raw = localStorage.getItem('atlas-v2-last-products');
      if (!raw) return [];
      const ids = JSON.parse(raw);
      if (!Array.isArray(ids)) return [];
      const byId = new Map(allProducts.map((p) => [p.id, p]));
      return ids
        .map((id: any) => byId.get(String(id)))
        .filter((p: MockProductV2 | undefined): p is MockProductV2 => !!p);
    } catch {
      return [];
    }
  }, [allProducts]);

  return (
    <div>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => {
          if (e.target.files && e.target.files.length > 0) {
            onStartUpload(e.target.files);
            // Reset el value para que si el user cancela y vuelve a
            // tocar Subir, el onChange dispare igual (sino con mismo
            // archivo no se gatilla de nuevo).
            e.target.value = '';
          }
        }}
      />
      <div className="flex items-start gap-3.5 mb-6">
        <div className="w-11 h-11 rounded-xl bg-brand-primary/10 flex items-center justify-center flex-shrink-0">
          <Upload className="w-5 h-5 text-brand-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[16px] font-semibold text-brand-black/90 leading-tight">
            Subí la imagen del producto
          </p>
          <p className="text-[13.5px] text-[#3C3C43]/60 mt-1 leading-relaxed">
            {presetKind === 'design-auto'
              ? 'Atlas va a usar esta foto y armar la escena entera con IA — sin configurar nada más.'
              : presetKind === 'design-modelo'
                ? 'Atlas va a usar esta foto y combinarla con el modelo, la pose y el fondo que elijas.'
                : 'Atlas va a usar esta foto para aplicar el preset elegido y armar la imagen final.'}
          </p>
        </div>
      </div>
      <div className="flex flex-col gap-2.5">
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="w-full inline-flex items-center justify-center gap-2.5 py-3.5 px-6 rounded-2xl bg-brand-primary hover:bg-brand-primary-hover text-white text-[15.5px] font-semibold shadow-sm transition-colors"
        >
          <Upload className="w-5 h-5" />
          Subir imagen
        </button>
        <button
          type="button"
          onClick={onShowProducts}
          className="w-full inline-flex items-center justify-center gap-2.5 py-3.5 px-6 rounded-2xl bg-white border border-neutral-200 hover:border-brand-primary/40 text-brand-black/85 text-[15.5px] font-semibold transition-colors"
        >
          <Package className="w-5 h-5" />
          Ver mis productos
        </button>
        {lastProducts.length > 0 && (
          <button
            type="button"
            onClick={() => onUseLast(lastProducts)}
            className="w-full inline-flex items-center justify-center gap-2.5 py-3.5 px-6 rounded-2xl bg-white border border-neutral-200 hover:border-brand-primary/40 text-brand-black/85 text-[15.5px] font-semibold transition-colors"
          >
            {/* Stack de thumbs: si hay varios, se solapan con `-space-x-2`
                tipo acordeón. El primero es el "principal" y los demás
                asoman por debajo a la derecha. */}
            <span className="flex shrink-0 -space-x-2">
              {lastProducts.slice(0, 3).map((p, i) => (
                <span
                  key={p.id}
                  className="relative w-7 h-7 rounded-lg overflow-hidden bg-neutral-100 ring-2 ring-white"
                  style={{ zIndex: 10 - i }}
                >
                  <img
                    src={p.imageUrl}
                    alt={p.name}
                    className="w-full h-full object-cover"
                  />
                </span>
              ))}
            </span>
            {lastProducts.length === 1
              ? 'Seleccionar producto anterior'
              : 'Seleccionar productos anteriores'}
          </button>
        )}
      </div>
    </div>
  );
};
